import { useRouteNavigator } from '@vkontakte/vk-mini-apps-router';
import type { NavIdProps } from '@vkontakte/vkui';
import { useState } from 'react';
import { ApiError, api, errorMessage } from '../../api/client';
import { launchParams, useSession, type Session } from '../../api/session';
import type { RegisterBody, RegisteredResponse } from '../../api/types';
import { t } from '../../i18n';
import { ImageIcon, PlusIcon, UsersIcon, WarningIcon } from '../../shared/icons';
import { digitGroups } from '../../shared/lib/docNumber';
import { getDisplayName } from '../../shared/lib/vkBridge';
import {
  Action,
  ActionRow,
  Card,
  CardBanner,
  CardBody,
  CardHeading,
  CardList,
  Gap,
  Gutter,
  Heading,
  Muted,
  NoticeBlock,
  Prose,
  Screen,
  Section,
  SectionTitle,
  StepList,
} from '../../shared/ui';

/**
 * Вкладка «Мой персонаж» для того, кого ещё не добавили в список участников.
 *
 * Пустой экран без объяснений — худшее, что можно показать человеку, который
 * только что открыл приложение и не понимает, почему у него ничего нет. Здесь
 * два сообщения: читать можно всё, а чтобы завести своего персонажа, нужен
 * администратор. Плюс номер страницы — его администратор и просит, потому что
 * короткое имя человек может сменить, а номер нет.
 *
 * Порядок на экране отвечает на вопросы в том порядке, в каком они возникают:
 * почему пусто → что тогда открыто прямо сейчас → что делать → какой номер
 * назвать. Поэтому переходы в реестр и внешки стоят сразу под объяснением, а
 * не кнопками в самом низу, куда до них ещё надо долистать.
 *
 * Когда включена самостоятельная запись, шагов «напишите администрации» и
 * номера страницы на экране НЕТ: и то и другое — обходной путь к
 * администратору, а он в этом режиме не нужен. Вместо них одна кнопка.
 * Показывается она гостю и тому, кто сам удалил свои данные: у второго сервер
 * снова откроет прежнюю строку. Забаненного («Снять доступ») сессия не узнаёт,
 * он выглядит гостем и кнопку видит — сервер ответит 403 `account_revoked`.
 * Известному аккаунту без права записи и без отметки удаления (`readOnly`)
 * кнопку не рисуем: сервер ответил бы 409.
 */

const ACCESS_STEPS: readonly { title: string; hint: string }[] = [
  {
    title: t('character.guest.step.write.title'),
    hint: t('character.guest.step.write.hint'),
  },
  {
    title: t('character.guest.step.id.title'),
    hint: t('character.guest.step.id.hint'),
  },
  {
    title: t('character.guest.step.wait.title'),
    hint: t('character.guest.step.wait.hint'),
  },
];

export function GuestOnboardingPanel({ id, onJoined }: NavIdProps & { onJoined: () => void }) {
  const routeNavigator = useRouteNavigator();
  const session = useSession();
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const state = guestState(session);
  const canJoin = state === 'join' || state === 'rejoin';

  /**
   * Заявки тут нет: запись или открыта, или нет. Имя берём у моста, номер
   * страницы сервер достанет из подписи параметров запуска — своему `vkUserId`
   * с экрана он не поверит.
   */
  const join = async () => {
    setJoining(true);
    setError(null);
    try {
      const body: RegisterBody = {
        launchParams: launchParams(),
        displayName: await getDisplayName(),
      };
      await api.post<RegisteredResponse>('/api/users/register', body);
      // Роль, вкладки и состав экранов приложение считает один раз на старте —
      // поэтому не дорисовываем хаб на месте, а перезапускаем сессию.
      onJoined();
    } catch (cause: unknown) {
      setError(joinFailure(cause));
    } finally {
      setJoining(false);
    }
  };

  return (
    <Screen id={id} title={t('character.title.hub')}>
      <Section>
        <CardList>
          {/* Состояние — полосой поверх карточки, а не значком в строке
              заголовка: эта карточка целиком и есть сообщение о состоянии. */}
          <Card tone={canJoin ? 'published' : 'pending'} padded={false}>
            <CardBanner icon={canJoin ? <PlusIcon /> : <WarningIcon />}>{BANNER[state]}</CardBanner>
            <CardBody>
              <CardHeading>{HEADLINE[state]}</CardHeading>
              <Gap step="s2h" />
              <Prose>{EXPLANATION[state]}</Prose>
            </CardBody>
          </Card>
        </CardList>
      </Section>

      {/* Главный ход экрана стоит сразу под объяснением: переходы в реестр и
          внешки — это «а пока», и держать из-за них кнопку ниже нельзя. */}
      {canJoin ? (
        <Section>
          <SectionTitle accent="pink">{t('character.guest.section.join')}</SectionTitle>
          <CardList>
            <Card>
              <Action
                tone="action"
                size="l"
                stretched
                loading={joining}
                before={<PlusIcon />}
                onClick={() => void join()}
              >
                {t('character.guest.action.join')}
              </Action>
              <Gap step="s2h" />
              <Muted>
                {state === 'rejoin'
                  ? t('character.guest.join.hintReturn')
                  : t('character.guest.join.hint')}
              </Muted>
            </Card>
          </CardList>

          {error ? (
            <NoticeBlock tone="error" title={t('character.guest.join.failed')}>
              {error}
            </NoticeBlock>
          ) : null}
        </Section>
      ) : null}

      <Section>
        <SectionTitle>{t('character.guest.section.open')}</SectionTitle>
        <Gutter>
          <ActionRow>
            <Action
              tone="accent"
              size="l"
              stretched
              keepCase
              before={<UsersIcon />}
              onClick={() => routeNavigator.push('/')}
            >
              {t('character.guest.action.registry')}
            </Action>
            <Action
              tone="accent"
              size="l"
              stretched
              keepCase
              before={<ImageIcon />}
              onClick={() => routeNavigator.push('/faces')}
            >
              {t('character.guest.action.faces')}
            </Action>
          </ActionRow>
        </Gutter>
      </Section>

      {canJoin ? null : (
        <>
          {/* Показываем и тем, у кого доступ сняли: путь назад у них тот же —
          написать администрации. Раньше блок им просто не рисовался, и экран
          заканчивался ничем. */}
          <Section>
            <SectionTitle>
              {state === 'noAccess'
                ? t('character.guest.section.howToGet')
                : t('character.guest.section.howToReturn')}
            </SectionTitle>
            <Gutter>
              <StepList steps={ACCESS_STEPS} />
            </Gutter>
          </Section>

          <Section>
            <SectionTitle accent="pink">{t('character.guest.section.vkId')}</SectionTitle>
            <CardList>
              <Card tone="rejected">
                <Heading tone="pink">{digitGroups(session.vkUserId)}</Heading>
                <Gap step="s2" />
                <Muted>{t('character.guest.vkIdHint')}</Muted>
              </Card>
            </CardList>
          </Section>
        </>
      )}
    </Screen>
  );
}

type GuestState = 'join' | 'noAccess' | 'rejoin' | 'deleted' | 'readOnly';

/**
 * Экран стоит только у сессии без права записи (App.tsx), так что известный
 * аккаунт здесь всегда без него. Отозванного сессия не узнаёт: он — `user === null`.
 */
function guestState({ user, selfRegistration }: Session): GuestState {
  if (user === null) return selfRegistration ? 'join' : 'noAccess';
  if (user.deletedAt !== null) return selfRegistration ? 'rejoin' : 'deleted';
  return 'readOnly';
}

const BANNER: Record<GuestState, string> = {
  join: t('character.guest.banner.join'),
  noAccess: t('character.guest.banner.noAccess'),
  rejoin: t('character.guest.banner.join'),
  deleted: t('character.guest.banner.deleted'),
  readOnly: t('common.access.revoked'),
};

const HEADLINE: Record<GuestState, string> = {
  join: t('character.guest.title.join'),
  noAccess: t('character.guest.title.noAccess'),
  rejoin: t('character.guest.title.rejoin'),
  deleted: t('character.guest.title.deleted'),
  readOnly: t('character.guest.title.readOnly'),
};

const EXPLANATION: Record<GuestState, string> = {
  join: t('character.guest.text.join'),
  noAccess: t('character.guest.text.noAccess'),
  rejoin: t('character.guest.text.rejoin'),
  deleted: t('character.guest.text.deleted'),
  readOnly: t('character.guest.text.readOnly'),
};

/**
 * Отказы саморегистрации объясняем по-русски: это единственные ошибки,
 * которые игрок увидит ПЕРВЫМ экраном приложения, и английская строка с
 * сервера здесь стоит дороже, чем везде.
 */
function joinFailure(cause: unknown): string {
  const code = cause instanceof ApiError ? cause.code : null;
  if (code === 'account_revoked') return t('character.guest.join.revoked');
  if (code === 'account_already_whitelisted') return t('character.guest.join.already');
  if (code === 'self_registration_disabled') return t('character.guest.join.closed');
  return errorMessage(cause);
}
